/*
 * Copyright Higharc Inc. 2019
 *
 * This file implements a tetrahedron camera so that a cube map can be generated from just 4 scene renderings instead of 6.
 * The pixels in the render targets are not used as efficiently (the edges of the render target are very stretched),
 * but in a CPU-limited scenario using the tetra cam instead of a cube cam can be worth it.
 */
import { Object3D, Quaternion, PerspectiveCamera, RGBAFormat, Vector3, WebGLRenderTarget, NearestFilter } from "three";
import { BitmapUtil } from "./BitmapUtil";
import { CubeCameraUtil } from "./CubeCameraUtil";
import { TetrahedronUtil } from "./TetrahedronUtil";

const tempVector = new Vector3();
const worldPos = new Vector3();

type FaceIndexAndUV = {
  faceIndex: number;
  x: number;
  y: number;
};

export class TetrahedronCamera extends Object3D {
  [key: string]: any;

  constructor(near, far, resolution, renderTargetOptions) {
    super();

    renderTargetOptions = renderTargetOptions || {
      format: RGBAFormat,
      magFilter: NearestFilter,
      minFilter: NearestFilter,
      stencilBuffer: false,
    };

    this._renderTargetResolution = resolution;

    this.faceCamera = TetrahedronCamera.createFaceCamera(0, near, far);
    this.add(this.faceCamera);
    this.faceCameraDirections = [];
    for (let faceIndex = 0; faceIndex < 4; ++faceIndex) {
      const faceCameraDirection: any = {};
      faceCameraDirection.lookAtDirection = TetrahedronUtil.faceNormal(faceIndex);
      faceCameraDirection.renderTarget = new WebGLRenderTarget(resolution, resolution, renderTargetOptions);
      faceCameraDirection.renderTarget.texture.name = "TetrahedronCamera" + faceIndex;
      this.faceCameraDirections.push(faceCameraDirection);
    }
  }

  update(renderer, scene, clearColor, clearDepth, clearStencil) {
    if (this.parent === null) {
      this.updateMatrixWorld();
    }

    const currentRenderTarget = renderer.getRenderTarget();
    this.faceCamera.getWorldPosition(worldPos);

    for (let faceIndex = 0; faceIndex < 4; ++faceIndex) {
      renderer.setRenderTarget(this.faceCameraDirections[faceIndex].renderTarget, 0);

      if (clearColor || clearDepth || clearStencil) {
        renderer.clear(clearColor, clearDepth, clearStencil);
      }

      const lookDir = this.faceCameraDirections[faceIndex].lookAtDirection;
      this.faceCamera.lookAt(worldPos.x + lookDir.x, worldPos.y + lookDir.y, worldPos.z + lookDir.z);
      this.faceCamera.updateMatrixWorld();

      renderer.render(scene, this.faceCamera);
    }

    renderer.setRenderTarget(currentRenderTarget);
  }

  clearRenderTargets(renderer, color, depth, stencil) {
    const currentRenderTarget = renderer.getRenderTarget();

    for (let faceIndex = 0; faceIndex < 4; ++faceIndex) {
      renderer.setRenderTarget(this.faceCameraDirections[faceIndex].renderTarget, 0);
      renderer.clear(color, depth, stencil);
    }

    renderer.setRenderTarget(currentRenderTarget);
  }

  readAsTetrahedronMap(renderer, processFaceBuffer: any = undefined) {
    const numChannels = 4;
    const currentRenderTarget = renderer.getRenderTarget();

    const tetraFaces: any[] = [];
    const arrayConstructor = CubeCameraUtil.readArrayConstructorFromRenderTarget(
      this.faceCameraDirections[0].renderTarget,
    );
    for (let faceIndex = 0; faceIndex < 4; ++faceIndex) {
      const readBuffer = new arrayConstructor(
        this._renderTargetResolution * this._renderTargetResolution * numChannels,
      );
      renderer.setRenderTarget(this.faceCameraDirections[faceIndex].renderTarget, 0);
      renderer.readRenderTargetPixels(
        this.faceCameraDirections[faceIndex].renderTarget,
        0,
        0,
        this._renderTargetResolution,
        this._renderTargetResolution,
        readBuffer,
        0,
      );

      tetraFaces.push(
        processFaceBuffer !== undefined
          ? processFaceBuffer(readBuffer, this.faceCameraDirections[faceIndex].renderTarget.texture.colorSpace)
          : readBuffer,
      );
    }

    renderer.setRenderTarget(currentRenderTarget);

    return tetraFaces;
  }

  readAsCubeMap(renderer, cubeResolution, processCubeFaceBuffer) {
    cubeResolution = cubeResolution || Math.floor(this._renderTargetResolution / 2);
    const tetraFaces = this.readAsTetrahedronMap(renderer);
    const bufferColorSpace = this.faceCameraDirections[0].renderTarget.texture.colorSpace;
    const arrayConstructor = CubeCameraUtil.readArrayConstructorFromRenderTarget(
      this.faceCameraDirections[0].renderTarget,
    );
    return TetrahedronCamera.convertTetraFacesToCubeFaces(
      tetraFaces,
      4,
      this._renderTargetResolution,
      cubeResolution,
      processCubeFaceBuffer,
      bufferColorSpace,
      arrayConstructor,
    );
  }

  dispose() {
    for (const faceCameraDir of this.faceCameraDirections) {
      faceCameraDir.renderTarget.dispose();
    }
  }
  static createFaceCamera(faceIndex: number, near: number, far: number): PerspectiveCamera {
    // Let's consider the bottom face of the tetrahedron with edge length a.
    // The tetrahedron insphere radius is a / sqrt( 24 ). This is the distance of the camera from the bottom face.
    // The vertical fov needs to cover the vertex of the triangle that's furthest away in the z direction.
    // The z coordinate of that vertex is -a / Math.sqrt( 3 ).
    // We get the vertical fov from the triangle defined by these two parameters.

    const triHeight = 1 / Math.sqrt(24);
    const triWidth = 1 / Math.sqrt(3);
    const halfVerticalFovRad = Math.atan(triWidth / triHeight);
    const verticalFov = (2 * halfVerticalFovRad * 180) / Math.PI;

    // The aspect ratio is the edge length divided by 2 / sqrt( 3 ).
    const aspect = Math.sqrt(3) / 2;

    const camera = new PerspectiveCamera(verticalFov, aspect, near, far);
    camera.lookAt(TetrahedronUtil.faceNormal(faceIndex));
    return camera;
  }

  static directionToFaceIndexAndUV(direction: Vector3, faceIndexAndUV: FaceIndexAndUV): void {
    let faceIndex = 0;
    let largestDot = 0;
    for (let i = 0; i < 4; ++i) {
      const candidateDot = direction.dot(faceNormals[i]);
      if (candidateDot > largestDot) {
        faceIndex = i;
        largestDot = candidateDot;
      }
    }

    // Rotate the direction vector so that it's relative to camera that's facing negative z.
    tempVector.copy(direction);
    tempVector.applyQuaternion(inverseCamQuaternions[faceIndex]);

    const u = -(tempVector.x / tempVector.z) / faceCameraWidth + 0.5;
    const v = -(tempVector.y / tempVector.z) / faceCameraHeight + 0.5;

    faceIndexAndUV.faceIndex = faceIndex;
    faceIndexAndUV.x = u;
    faceIndexAndUV.y = v;
  }

  static convertTetraFacesToCubeFaces(
    tetraFaces,
    numChannels,
    tetraResolution,
    cubeResolution,
    processCubeFaceBuffer,
    bufferColorSpace,
    arrayConstructor,
  ) {
    const texelHalfWidth = 1.0 / cubeResolution;
    const cubeFaces: any[] = [];
    const tetraFaceIndexAndUV: FaceIndexAndUV = { faceIndex: 0, x: 0, y: 0 };
    const mappingCacheKey = `${tetraResolution}:${cubeResolution}`;
    let cachedIndexMapping: number[] = [];
    if (cachedTetrahedronMapToCubeMapIndices.hasOwnProperty(mappingCacheKey)) {
      cachedIndexMapping = cachedTetrahedronMapToCubeMapIndices[mappingCacheKey];
    } else {
      let cubePixelIndex = 0;
      for (let cubeFaceIndex = 0; cubeFaceIndex < 6; ++cubeFaceIndex) {
        let texelCenterY = 1.0 - texelHalfWidth;
        for (let intY = 0; intY < cubeResolution; ++intY) {
          let texelCenterX = -1.0 + texelHalfWidth;
          for (let intX = 0; intX < cubeResolution; ++intX) {
            CubeCameraUtil.getCubeMapTexelDirection(cubeFaceIndex, texelCenterX, texelCenterY, tempVector);
            TetrahedronCamera.directionToFaceIndexAndUV(tempVector, tetraFaceIndexAndUV);

            cachedIndexMapping[cubePixelIndex * 3] = tetraFaceIndexAndUV.faceIndex;
            cachedIndexMapping[cubePixelIndex * 3 + 1] = tetraFaceIndexAndUV.x;
            cachedIndexMapping[cubePixelIndex * 3 + 2] = tetraFaceIndexAndUV.y;

            texelCenterX += texelHalfWidth * 2.0;
            ++cubePixelIndex;
          }
          texelCenterY -= texelHalfWidth * 2.0;
        }
        cachedTetrahedronMapToCubeMapIndices[mappingCacheKey] = cachedIndexMapping;
      }
    }
    let cubePixelIndex = 0;
    const uv = {
      x: 0,
      y: 0,
    };
    const linearSample = new arrayConstructor(numChannels);
    for (let cubeFaceIndex = 0; cubeFaceIndex < 6; ++cubeFaceIndex) {
      const cubeFace = new arrayConstructor(cubeResolution * cubeResolution * numChannels);
      for (let pixelIndex = 0, pixelLen = cubeResolution * cubeResolution; pixelIndex < pixelLen; ++pixelIndex) {
        const cubeFaceDataIndex = pixelIndex * numChannels;
        uv.x = cachedIndexMapping[cubePixelIndex * 3 + 1];
        uv.y = cachedIndexMapping[cubePixelIndex * 3 + 2];
        // Linear filter the tetrahedral rendering to get the cube map value.
        // Note that we don't perform filtering between different tetrahedron faces, but that isn't much of an
        // issue in practice since the faces have extra data outside the bounds of the tetrahedron.
        BitmapUtil.linearFilteredSample(
          tetraFaces[cachedIndexMapping[cubePixelIndex * 3]],
          tetraResolution,
          tetraResolution,
          numChannels,
          uv,
          linearSample,
        );
        for (let i = 0; i < numChannels; ++i) {
          cubeFace[cubeFaceDataIndex + i] = linearSample[i];
        }
        ++cubePixelIndex;
      }
      cubeFaces.push(
        processCubeFaceBuffer !== undefined ? processCubeFaceBuffer(cubeFace, bufferColorSpace) : cubeFace,
      );
    }

    return cubeFaces;
  }
}

// The near and far plane values for mathFaceCameras don't really matter.
// We just need the rotation quaternions and aspect ratio to be able to do calculations related to the cameras.
const mathFaceCameras = [
  TetrahedronCamera.createFaceCamera(0, 1, 10),
  TetrahedronCamera.createFaceCamera(1, 1, 10),
  TetrahedronCamera.createFaceCamera(2, 1, 10),
  TetrahedronCamera.createFaceCamera(3, 1, 10),
];

const inverseCamQuaternions = [new Quaternion(), new Quaternion(), new Quaternion(), new Quaternion()];

for (let i = 0; i < 4; ++i) {
  inverseCamQuaternions[i].copy(mathFaceCameras[i].quaternion);
  inverseCamQuaternions[i].invert();
}

const faceNormals = [
  TetrahedronUtil.faceNormal(0),
  TetrahedronUtil.faceNormal(1),
  TetrahedronUtil.faceNormal(2),
  TetrahedronUtil.faceNormal(3),
];

const faceCameraHeight = (2 * (1 / Math.sqrt(3))) / (1 / Math.sqrt(24));
const faceCameraWidth = mathFaceCameras[0].aspect * faceCameraHeight;

const cachedTetrahedronMapToCubeMapIndices: Record<string, number[]> = {};
